import { useCallback, useRef, useState } from 'react';
import { ReactComponent as CriticalAssetUnsetIcon } from '@elementary/common-ui/assets/16x16/critical-asset-tag-unmark.svg';
import { ReactComponent as CriticalAssetIcon } from '@elementary/common-ui/assets/16x16/critical-asset-tag.svg';
import { ReactComponent as CrossIcon } from '@elementary/common-ui/assets/cross.svg';
import { ReactComponent as DocIcon } from '@elementary/common-ui/assets/document.svg';
import { ReactComponent as FilterIcon } from '@elementary/common-ui/assets/filter-2.svg';
import { ReactComponent as MoreIcon } from '@elementary/common-ui/assets/more.svg';
import {
  Button,
  FlexCenter,
  FlexColumn,
} from '@elementary/common-ui/components';
import useHover from '@elementary/common-ui/hooks/useHover';
import useSelectedTreeItem from '@elementary/common-ui/hooks/useSelectedTreeItem';
import { Menu, Typography, styled } from '@mui/material';
import { NodeProps } from 'reactflow';
import { getTreeTypeByArtifactType } from '@elementary/common-ui/utils/common';
import { ReactFlowNodeData } from './types';
import { useLineageGraphContext } from './LineageGraphContext';

const MenuButton = styled(FlexCenter)<{
  $visible: boolean;
  $isParent: boolean;
  $isSource: boolean;
  $isExposure: boolean;
  $isChild: boolean;
}>`
  position: absolute;
  right: ${({ $visible }) => ($visible ? -36 : 0)}px;
  top: 0;
  width: 40px;
  height: ${({ $isParent, $isChild }) =>
    $isParent ? 42 : $isChild ? 40 : 40}px;
  border-radius: 0 8px 8px 0;
  transition: background-color 150ms, right 80ms;
  cursor: pointer;
  z-index: -1;

  background: ${({ theme, $isSource, $isExposure, $visible }) =>
    !$visible
      ? 'transparent'
      : $isSource
      ? theme.palette.common.lightBlue4
      : $isExposure
      ? theme.palette.common.lightPurple
      : theme.palette.common.gray10};

  border: ${({ theme, $isSource, $isExposure, $visible }) =>
    `2px solid ${
      !$visible
        ? 'transparent'
        : $isSource
        ? theme.palette.common.blue2
        : $isExposure
        ? theme.palette.common.purple
        : theme.palette.common.gray14
    }`};

  border-left: none;
`;

const IconWrapper = styled(FlexCenter)`
  width: 20px;
  height: 20px;
`;

const MenuItem = styled(Button)`
  border-radius: 0 !important;
  padding: 8px 16px !important;
  height: 36px !important;
  &.MuiButtonBase-root {
    justify-content: flex-start;
  }
  .MuiButton-startIcon {
    margin: 0 16px 0 0px;
  }
`;

export type Props = {
  node?: NodeProps<ReactFlowNodeData>;
  showMenuButton: boolean;
  pos: { left: number; top: number } | null;
  onOpen: (e: any) => void;
  onClose: () => void;
  onHighlightNodeClick: () => void;
  isCriticalAsset?: boolean;
  onSetIsCriticalAssets?: () => void;
  showCriticalAssetOption?: boolean;
  showTestResults?: boolean;
};

const NodeContextMenu = ({
  node,
  showMenuButton,
  pos,
  onOpen,
  onClose,
  onHighlightNodeClick,
  isCriticalAsset,
  onSetIsCriticalAssets,
  showCriticalAssetOption,
  showTestResults,
}: Props) => {
  const { highlightedNode, setLineageInfoPanel } = useLineageGraphContext();
  const [menuButtonClicked, setMenuButtonClicked] = useState(false);

  const menuButtonRef = useRef<any>();
  const isMenuButtonHover = useHover(menuButtonRef);

  const { setSelectedTreeModel } = useSelectedTreeItem();

  const handleMenuButtonClick = (e: any) => {
    onOpen(e);
    setMenuButtonClicked(true);
  };

  const handleClose = useCallback(
    (e: any) => {
      e.stopPropagation();
      onClose();
      setMenuButtonClicked(false);
    },
    [onClose],
  );

  const handleSelectNodeClick = useCallback(
    (e: any, option: 'testResults' | 'nodeInfo') => {
      if (node) {
        setLineageInfoPanel({ isOpen: true, tab: option });
        if (node.id !== highlightedNode?.id) {
          onHighlightNodeClick();
        }
      }
      handleClose(e);
    },
    [
      handleClose,
      highlightedNode,
      node,
      onHighlightNodeClick,
      setLineageInfoPanel,
    ],
  );

  const handleFilterNodeClick = useCallback(
    (e: any) => {
      handleClose(e);
      if (!node) return;

      const treeType = getTreeTypeByArtifactType(node.data.metadata.type);
      const nodeSourceType =
        node.data.metadata.sourceType || node.data.parentNode?.sourceType;
      const isDbtSourced = nodeSourceType === 'dbt';
      const treeViewBy = isDbtSourced ? 'dbt' : 'dwh';

      const columnType = node.data.metadata.type;

      if (!isDbtSourced && node.data.parentNode) {
        // For non-dbt child nodes (e.g. elements inside semantic views),
        // resolve to the top-level ancestor that exists in the DWH tree.
        const topLevelId = node.data.parentNode.parent
          ? node.data.parentNode.parent
          : node.data.parentNode.id;
        setSelectedTreeModel(
          topLevelId,
          node.id,
          treeType,
          treeViewBy,
          columnType,
        );
      } else if (node.data.parentNode) {
        setSelectedTreeModel(
          node.data.parentNode.id,
          node.id,
          treeType,
          treeViewBy,
          columnType,
        );
      } else {
        setSelectedTreeModel(node.id, undefined, treeType, treeViewBy);
      }
    },
    [handleClose, node, setSelectedTreeModel],
  );

  const handleHighlightNodeClick = useCallback(
    (e: any) => {
      handleClose(e);
      if (!node || node.id === highlightedNode?.id) return;
      onHighlightNodeClick();
    },
    [handleClose, highlightedNode, node, onHighlightNodeClick],
  );

  const handleSetAsCriticalAsset = useCallback(
    (e: any) => {
      onSetIsCriticalAssets?.();
      handleClose(e);
    },
    [handleClose, onSetIsCriticalAssets],
  );

  const isChild = !!node?.data.parentNode;
  const isParent = !!node?.data.children;
  const isElement =
    isChild && node?.data.metadata.type !== 'column' && !!node?.data.children;

  return (
    <>
      <MenuButton
        ref={menuButtonRef}
        onClick={handleMenuButtonClick}
        $visible={showMenuButton || isMenuButtonHover || menuButtonClicked}
        $isParent={isParent}
        $isSource={node?.data.metadata.type === 'source'}
        $isExposure={node?.data.metadata.type === 'exposure'}
        $isChild={isChild}
      >
        <MoreIcon />
      </MenuButton>
      <Menu
        open={!!pos}
        onClose={handleClose}
        anchorPosition={{ left: pos?.left || 0, top: (pos?.top || 0) + 10 }}
        anchorReference="anchorPosition"
      >
        <FlexColumn sx={{ pt: 0.5, gap: 1 }}>
          <Typography
            sx={{
              fontSize: '12px',
              color: 'common.gray11',
              fontWeight: 'bold',
              px: 2,
            }}
          >
            ACTIONS
          </Typography>

          {node && showCriticalAssetOption && (
            <MenuItem
              color="secondary"
              onClick={handleSetAsCriticalAsset}
              startIcon={
                <IconWrapper>
                  {isCriticalAsset ? (
                    <CriticalAssetUnsetIcon />
                  ) : (
                    <CriticalAssetIcon />
                  )}
                </IconWrapper>
              }
            >
              {`${isCriticalAsset ? 'Unset' : 'Set'} as Critical Asset`}
            </MenuItem>
          )}

          <MenuItem
            color="secondary"
            startIcon={
              <IconWrapper>
                <CrossIcon />
              </IconWrapper>
            }
            onClick={handleHighlightNodeClick}
          >
            {isChild && !isElement ? 'Highlight column' : 'Highlight node'}
          </MenuItem>

          <MenuItem
            color="secondary"
            startIcon={
              <IconWrapper>
                <FilterIcon />
              </IconWrapper>
            }
            onClick={handleFilterNodeClick}
          >
            {isChild && !isElement ? 'Filter column' : 'Filter node'}
          </MenuItem>

          {node &&
            node?.data.metadata.type !== 'exposure' &&
            node.data.parentNode?.type !== 'exposure' &&
            showTestResults && (
              <MenuItem
                color="secondary"
                startIcon={
                  <IconWrapper>
                    <DocIcon />
                  </IconWrapper>
                }
                onClick={(e: any) => handleSelectNodeClick(e, 'testResults')}
              >
                Test results
              </MenuItem>
            )}
          {node && !node.data.parentNode && (
            <MenuItem
              color="secondary"
              startIcon={
                <IconWrapper>
                  <DocIcon />
                </IconWrapper>
              }
              onClick={(e: any) => handleSelectNodeClick(e, 'nodeInfo')}
            >
              Node info
            </MenuItem>
          )}
        </FlexColumn>
      </Menu>
    </>
  );
};

export default NodeContextMenu;
